import { useRoute } from '@shared/composables/useRoute';
import { PayoutEvent, type IPayoutEvent } from '@app/purchase/bundle/models/PayoutEvent.ts';
import type { FormOptions } from '@shared/types/Form';
import { useInjectErrorState } from '@shared/composables/useProvideErrorState';
import { useForm } from '@shared/composables/useForm';
import { useWithValidationConfig } from '@shared/composables/useWithValidationConfig';
import { usePayload } from '@shared/composables/usePayload';
import type { ListResponse } from '@shared/types/ApiResponse.ts';
import type { EligibleOwner } from '@app/purchase/bundle/routes/routes.ts';
import { router } from '@inertiajs/vue3';
import type { VisitOptions } from '@inertiajs/core';
import { useRouter } from '@shared/composables/useRouter.ts';

export function usePayoutEventRepository<T extends IPayoutEvent = IPayoutEvent>() {
    const command = () => {
        const store = (data: T, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<T>(data).precognition(
                PayoutEvent.routes().store,
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.transform((data) => usePayload<T>(data)).submit(options);
            }

            return { form, execute };
        };

        const update = (id: string, data: T, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<T>(data).precognition(
                {
                    url: useRoute().build(PayoutEvent.routes().update, {
                        id,
                    }),
                    method: PayoutEvent.routes().update.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.transform((data) =>
                    usePayload<T>(data, {
                        only: ['name', 'steward_id', 'date', 'payout_price'],
                        nullable: ['date', 'payout_price'],
                    }),
                ).submit(options);
            }

            return { form, execute };
        };

        const destroy = <U extends { id: string }>(id: string, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<U>({ id } as U).precognition(
                {
                    url: useRoute().build(PayoutEvent.routes().destroy, {
                        id,
                    }),
                    method: PayoutEvent.routes().destroy.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.submit(options);
            }

            return { form, execute };
        };

        const confirm = (payoutEventId: string) => {
            function execute(options?: VisitOptions) {
                router.post(route('bundles.confirm', { id: payoutEventId }), {}, options);
            }

            return { execute };
        };

        const detachOwner = (payoutEventId: string, ownerId: string) => {
            function execute(options?: VisitOptions) {
                router.delete(route('bundles.detach-owner', { id: payoutEventId, ownerId }), options);
            }

            return { execute };
        };

        return {
            store,
            update,
            destroy,
            confirm,
            detachOwner,
        };
    };

    const query = () => {
        const show = (id: string) => {
            return { endpoint: useRoute().build(PayoutEvent.routes().show, { id }) };
        };

        const eligibleOwners = (bundleId: string) => {
            return useRouter().get<ListResponse<EligibleOwner>>(route('bundles.eligible-owners', { id: bundleId }));
        };

        return {
            show,
            eligibleOwners,
        };
    };

    return {
        command: command(),
        query: query(),
    };
}
